/**
 * Direct manipulation of the deck.
 *
 * Pointer events are taken in the capture phase so a grab on a deck part is
 * decided before OrbitControls sees it — and the camera is always handed back
 * when the gesture ends, however it ends.
 */

import { DIM } from '../geometry/dims.ts';
import { isTouch } from '../lib/device.ts';
import * as player from '../state/player.ts';
import type { HitType } from '../three/parts/hits.ts';
import { grabArm, moveArm, releaseArm } from './arm-drag.ts';
import { toggleTransport } from './commands.ts';
import { applyPitch, setRPM, toggleCue, togglePower } from './deck-commands.ts';
import { hardware } from './hardware.ts';
import { pickAt, planeAt } from './picking.ts';
import { beginScratch, endScratch, moveScratch, SCRATCH_PLANE_Y } from './scratch.ts';

/** Pixels of travel below which a drag still counts as a tap. */
const TAP_SLOP = 8;

interface Drag {
  type: HitType;
  value?: number;
  pointerId: number;
  moved: number;
  lastX: number;
  lastY: number;
}

let drag: Drag | null = null;

/** Always hand the camera back, whatever happened during the gesture. */
function endGesture(): void {
  drag = null;
  const { stage } = hardware();
  stage.controls.enabled = true;
  stage.renderer.domElement.style.cursor = 'default';
}

function onDown(e: PointerEvent): void {
  if (e.button > 0) return;
  if (drag) {
    /* A second finger changes nothing: a hand on the tonearm does not let go
       because another finger brushed the plinth. The same pointer coming down
       again means its release was never seen, so that gesture is over. */
    if (e.pointerId !== drag.pointerId) return;
    finish(true);
  }

  const pick = pickAt(e.clientX, e.clientY);
  if (!pick) return;
  if (pick.hit.type === 'arm' && !grabArm(e.clientX, e.clientY)) return;
  if (pick.hit.type === 'disc') beginScratch(pick.point);

  drag = {
    type: pick.hit.type,
    ...(pick.hit.value === undefined ? {} : { value: pick.hit.value }),
    pointerId: e.pointerId,
    moved: 0,
    lastX: e.clientX,
    lastY: e.clientY,
  };
  const { stage } = hardware();
  stage.controls.enabled = false;
  const canvas = stage.renderer.domElement;
  canvas.style.cursor = 'grabbing';
  /* Capture, so a release outside the window — or a pointer the browser takes
     away — still reaches us, as a pointerup or a lostpointercapture. */
  try {
    canvas.setPointerCapture(e.pointerId);
  } catch {
    /* an inactive pointer cannot be captured; the window listeners still see it */
  }
}

function onMove(e: PointerEvent): void {
  const d = drag;
  if (!d || e.pointerId !== d.pointerId) return;
  d.moved += Math.abs(e.clientX - d.lastX) + Math.abs(e.clientY - d.lastY);
  d.lastX = e.clientX;
  d.lastY = e.clientY;

  if (d.type === 'disc') {
    const p = planeAt(SCRATCH_PLANE_Y, e.clientX, e.clientY);
    if (p) moveScratch(p);
  } else if (d.type === 'arm') {
    moveArm(e.clientX, e.clientY);
  } else if (d.type === 'pitch') {
    const p = planeAt(DIM.top + 1, e.clientX, e.clientY);
    if (p) applyPitch(hardware().tt.pitchFromPoint(p), false);
  }
}

function onTap(type: HitType, value: number | undefined): void {
  const { deck } = player;
  const click = (pitch: number) => deck.demo.sfxClick(pitch).catch(() => undefined);
  if (type === 'start') {
    void click(1);
    void toggleTransport();
  } else if (type === 'speed') {
    void click(1.3);
    setRPM(value ?? 33);
  } else if (type === 'power') {
    void click(0.7);
    togglePower();
  } else if (type === 'cue') {
    void click(0.9);
    toggleCue();
  }
}

/**
 * Ends the gesture in hand. A held part is let go where it is whether the
 * pointer was released or taken away; only a tap needs a clean release.
 */
function finish(interrupted: boolean): void {
  const d = drag;
  if (!d) return;
  endGesture();
  if (d.type === 'disc') endScratch();
  else if (d.type === 'arm') releaseArm();
  else if (d.type === 'pitch') applyPitch(hardware().tt.state.pitch, true);
  else if (!interrupted && d.moved < TAP_SLOP) onTap(d.type, d.value);
}

/** pointerup, pointercancel and lostpointercapture all arrive here. */
function onUp(e: PointerEvent): void {
  if (!drag || e.pointerId !== drag.pointerId) return;
  finish(e.type !== 'pointerup');
}

/** Wires the deck's direct manipulation. Returns a teardown function. */
export function wireGestures(): () => void {
  const canvas = hardware().stage.renderer.domElement;

  const hover = (e: PointerEvent): void => {
    if (drag) return;
    canvas.style.cursor = pickAt(e.clientX, e.clientY) ? 'grab' : 'default';
  };
  /* A window that loses focus mid-drag may never be told the pointer went up. */
  const onBlur = (): void => finish(true);

  canvas.addEventListener('pointerdown', onDown, true);
  canvas.addEventListener('lostpointercapture', onUp);
  addEventListener('pointermove', onMove);
  addEventListener('pointerup', onUp);
  addEventListener('pointercancel', onUp);
  addEventListener('blur', onBlur);
  if (!isTouch) canvas.addEventListener('pointermove', hover);

  return () => {
    canvas.removeEventListener('pointerdown', onDown, true);
    canvas.removeEventListener('lostpointercapture', onUp);
    removeEventListener('pointermove', onMove);
    removeEventListener('pointerup', onUp);
    removeEventListener('pointercancel', onUp);
    removeEventListener('blur', onBlur);
    canvas.removeEventListener('pointermove', hover);
  };
}
